import { isFmeaRiskList, Policy } from "@design-controls/types";
import {
  QButton,
  QDeleteConfirmationModal,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QText,
} from "@qualio/ui-components";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRetrieveElements } from "../../../hooks/designElement/useRetrieveEleements";
import { useCurrentConfigs } from "../../../hooks/useCurrentConfigs";
import { useDeletePolicy } from "../../../hooks/policy/useDeletePolicy";

type Props = {
  type: Policy["type"];
  onClose: () => void;
  onDelete?: () => void;
};

export const DeletePolicy: React.FC<Props> = ({ type, onClose, onDelete }) => {
  const { t } = useTranslation();
  const { data, isLoading } = useRetrieveElements();
  const policy = useCurrentConfigs().find((t) => t.type === type);
  const { isLoading: isDeleting, mutate } = useDeletePolicy();

  const elementsExist = useMemo(() => {
    if (type === "risk") {
      return !!data?.filter(isFmeaRiskList).length;
    }
    return data?.find((item) => {
      return (
        item.type === type ||
        (type === "req2" && item.type === "testCase2") ||
        (type === "req3" && item.type === "testCase3")
      );
    });
  }, [type, data]);

  if (isLoading || !policy) {
    return null;
  }

  if (elementsExist) {
    return (
      <QModal isOpen={true} onClose={onClose}>
        <QModalHeader>
          <QText data-cy="policy-in-use">
            {t("policies.delete.inUseTitle")}
          </QText>
        </QModalHeader>
        <QModalBody>
          {t("policies.delete.inUseMessage", {
            policyType:
              type === "risk" ? t("policies.delete.fmeaRisks") : policy.label,
          })}
        </QModalBody>
        <QModalActions>
          <QButton onClick={onClose} data-cy="policy-in-use-btn">
            {t("policies.delete.actions.ok")}
          </QButton>
        </QModalActions>
      </QModal>
    );
  }

  return (
    <QDeleteConfirmationModal
      onConfirm={() => {
        if (!!onDelete) {
          onDelete();
        } else {
          mutate(type);
        }
      }}
      onClose={onClose}
      title={t("policies.delete.modalTitle", { policyLabel: policy.label })}
      inProgress={isDeleting}
      message={
        <QText fontSize="sm">{t("policies.delete.confirmMessage")}</QText>
      }
    />
  );
};
